import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, authHeaders, authStorageKey } from '../api/client';

const AuthContext = createContext(undefined);

function readStoredToken() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.sessionStorage.getItem(authStorageKey) || '';
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => readStoredToken());
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const persistToken = useCallback((nextToken) => {
    if (nextToken) {
      window.sessionStorage.setItem(authStorageKey, nextToken);
    } else {
      window.sessionStorage.removeItem(authStorageKey);
    }
    setToken(nextToken);
  }, []);

  const signOut = useCallback(() => {
    persistToken('');
    setCurrentUser(null);
    setAuthError('');
  }, [persistToken]);

  const signIn = useCallback(async (form) => {
    setIsAuthLoading(true);
    setAuthError('');
    try {
      const response = await api.post('/api/auth/login', form);
      const access = response.data?.access_token;
      const user = response.data?.user;
      if (!access) {
        throw new Error('Login response missing access token');
      }
      persistToken(access);
      if (user) {
        setCurrentUser(user);
      } else {
        const me = await api.get('/api/auth/me', authHeaders(access));
        setCurrentUser(me.data);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Sign-in failed';
      setAuthError(detail);
      throw error;
    } finally {
      setIsAuthLoading(false);
    }
  }, [persistToken]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const me = await api.get('/api/auth/me', authHeaders(token));
        if (!cancelled) {
          setCurrentUser(me.data);
        }
      } catch {
        if (!cancelled) {
          persistToken('');
          setCurrentUser(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, persistToken]);

  const value = useMemo(() => ({ token, currentUser, isAuthLoading, authError, signIn, signOut }), [
    token,
    currentUser,
    isAuthLoading,
    authError,
    signIn,
    signOut,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
